import React, {useEffect, useId, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDoorOpen, faChevronDown, faCheck} from '@fortawesome/free-solid-svg-icons';
import {useClickOutside} from '../../hooks/useClickOutside';

export interface RoomOption {
    id: string;
    name: string;
    description?: string;
}

/** Chiều cao tối đa của danh sách thả xuống, khớp với `max-h-72` bên dưới. */
const PANEL_MAX_HEIGHT = 288;

interface RoomPickerProps {
    rooms: RoomOption[];
    value: string;
    onChange: (roomId: string) => void;
    placeholder?: string;
}

/**
 * Ô chọn phòng của form đăng ký nhận phòng.
 *
 * Cố ý KHÔNG dùng <select>: option của select chỉ hiện được một dòng chữ trơn,
 * nên mô tả phòng ("Tầng 2, cạnh cầu thang") phải đẩy xuống dưới ô và chỉ hiện
 * SAU khi đã chọn. Người thuê mới thì nhớ phòng theo mô tả chứ không nhớ id -
 * bắt họ bấm thử từng phòng để đọc mô tả là ngược. Danh sách tự vẽ cho phép mỗi
 * dòng hiện cả tên lẫn mô tả, người ta đọc một lượt là thấy phòng mình.
 *
 * Đây là form người thuê điền đúng một lần trong đời và hầu hết điền trên điện
 * thoại, nên không có ô tìm kiếm: nhà chỉ vài chục phòng, cuộn là hết.
 */
export const RoomPicker: React.FC<RoomPickerProps> = ({rooms, value, onChange, placeholder = 'Chọn phòng bạn đang thuê'}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [dropUp, setDropUp] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const activeOptionRef = useRef<HTMLButtonElement>(null);
    const listId = useId();

    const selectedIndex = rooms.findIndex(room => room.id === value);
    const selected = selectedIndex >= 0 ? rooms[selectedIndex] : undefined;

    useClickOutside(containerRef, isOpen, () => setIsOpen(false));

    const open = () => {
        // Mở ra là con trỏ nằm sẵn ở phòng đang chọn, không phải ở đầu danh sách -
        // người đang sửa lại lựa chọn thì thấy ngay mình đang đứng ở đâu.
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        // Trên điện thoại ô này nằm giữa form, thả danh sách xuống dưới là nó
        // chạy quá mép màn hình. Chỗ nào rộng hơn thì mở về phía đó.
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropUp(spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow);
        }
        setIsOpen(true);
    };

    // Cuộn dòng đang trỏ vào tầm nhìn: danh sách có thanh cuộn, bấm mũi tên
    // xuống quá đáy mà khung không chạy theo thì coi như bấm vào hư không.
    useEffect(() => {
        if (isOpen) activeOptionRef.current?.scrollIntoView({block: 'nearest'});
    }, [isOpen, activeIndex]);

    const select = (roomId: string) => {
        onChange(roomId);
        setIsOpen(false);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            return;
        }
        if (!isOpen) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                open();
            }
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex(index => Math.min(index + 1, rooms.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        } else if (event.key === 'Home') {
            event.preventDefault();
            setActiveIndex(0);
        } else if (event.key === 'End') {
            event.preventDefault();
            setActiveIndex(rooms.length - 1);
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const room = rooms[activeIndex];
            if (room) select(room.id);
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={isOpen ? listId : undefined}
                aria-activedescendant={isOpen ? `${listId}-${activeIndex}` : undefined}
                disabled={rooms.length === 0}
                onClick={() => (isOpen ? setIsOpen(false) : open())}
                onKeyDown={onKeyDown}
                className="glass-input w-full flex items-center gap-3 pl-3.5 pr-3.5 py-2.5 text-left disabled:opacity-60"
            >
                <FontAwesomeIcon icon={faDoorOpen} className="text-slate-500 flex-shrink-0"/>
                <span className="min-w-0 flex-1">
                    {rooms.length === 0 ? (
                        <span className="block text-sm text-slate-500">Đang tải danh sách phòng…</span>
                    ) : selected ? (
                        <>
                            <span className="block text-sm font-medium text-slate-800 dark:text-white truncate">
                                {selected.name}
                            </span>
                            {selected.description && (
                                <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                                    {selected.description}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="block text-sm text-slate-500">{placeholder}</span>
                    )}
                </span>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-xs text-slate-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div
                    id={listId}
                    role="listbox"
                    aria-label="Danh sách phòng"
                    className={`absolute left-0 right-0 z-50 glass-panel overflow-y-auto scrollbar-thin max-h-72
                        ${dropUp ? 'bottom-full mb-2' : 'top-full mt-2'}`}
                >
                    {rooms.map((room, index) => {
                        const isSelected = room.id === value;
                        return (
                            <button
                                key={room.id}
                                ref={index === activeIndex ? activeOptionRef : null}
                                id={`${listId}-${index}`}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => select(room.id)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                                    ${index === activeIndex ? 'bg-slate-500/15' : 'hover:bg-white/50 dark:hover:bg-white/5'}`}
                            >
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-medium text-slate-800 dark:text-white truncate">
                                        {room.name}
                                    </span>
                                    {room.description && (
                                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                                            {room.description}
                                        </span>
                                    )}
                                </span>
                                {isSelected && (
                                    <FontAwesomeIcon icon={faCheck} className="text-xs text-violet-500 flex-shrink-0"/>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
